import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import useAccessToken from 'hooks/useAccessToken';
import useGlobalStore from 'hooks/useGlobalStore';
import usePageRoutes from 'hooks/usePageRoutes';

const OnboardingGuard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isChecking, setIsChecking] = useState(true);
    const [hasRedirected, setHasRedirected] = useState(false);
    const { decodedToken } = useAccessToken();
    const { getValue: getCurrentUserValue } = useGlobalStore({
        namespace: 'useCurrentUser'
    });
    const currentUser = getCurrentUserValue('currentUser');
    const hasCompleteProfile = getCurrentUserValue('hasCompleteProfile');

    const { getValue: getCurrentOrganizationValue } = useGlobalStore({
        namespace: 'useCurrentOrganization'
    });
    const currentOrganization = getCurrentOrganizationValue('currentOrganization');

    const hasOrganizationInToken = Boolean(
        decodedToken &&
        (decodedToken.org_id || (decodedToken as { organization_id?: string }).organization_id)
    );
    const { homePage, onboardingCurrentUserPage, onboardingOrganizationCreationPage } =
        usePageRoutes();

    const currentPage = location.pathname;
    const onboardingOrganizationPages = [onboardingOrganizationCreationPage];
    const hasOrganization = currentOrganization || hasOrganizationInToken;

    const shouldRedirectToHome =
        (hasCompleteProfile &&
            hasOrganization &&
            onboardingOrganizationPages.includes(currentPage)) ||
        (hasCompleteProfile && currentPage.includes(onboardingCurrentUserPage));

    const checkOnboardingStatus = async () => {
        if (hasCompleteProfile && currentPage.includes(onboardingCurrentUserPage)) {
            if (!hasRedirected) {
                setHasRedirected(true);
                setIsChecking(false);
                navigate(homePage, { replace: true });
            }

            return;
        }

        if (!hasCompleteProfile) {
            if (onboardingOrganizationPages.includes(currentPage)) {
                if (!hasRedirected) {
                    setHasRedirected(true);
                    setIsChecking(false);
                    navigate(onboardingCurrentUserPage, { replace: true });
                }

                return;
            }

            setIsChecking(false);
            setHasRedirected(false);

            return;
        }

        try {
            if (hasOrganization) {
                if (!hasRedirected) {
                    setHasRedirected(true);
                    setIsChecking(false);
                    navigate(homePage, { replace: true });
                }

                return;
            } else {
                setIsChecking(false);
                setHasRedirected(false);
            }
        } catch (error) {
            console.error('Error checking organization:', error);

            setIsChecking(false);
            setHasRedirected(false);
        }
    };

    useEffect(() => {
        if (currentUser !== undefined && !shouldRedirectToHome) {
            checkOnboardingStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentUser,
        location.pathname,
        decodedToken,
        hasCompleteProfile,
        currentOrganization,
        shouldRedirectToHome
    ]);

    useEffect(() => {
        setHasRedirected(false);
    }, [location.pathname]);

    useEffect(() => {
        if (shouldRedirectToHome && !hasRedirected) {
            setHasRedirected(true);
            setIsChecking(false);
            navigate(homePage, { replace: true });
        }
    }, [shouldRedirectToHome, hasRedirected, navigate, homePage, currentPage]);

    if (shouldRedirectToHome || isChecking) {
        return null;
    }

    return <Outlet />;
};

export default OnboardingGuard;
